import {
  EXTENDED_WAIT_THRESHOLD_MS,
  type EvaluationStreamProgress,
} from '@/student/task/stores/TaskSubmissionStore';
import type { AIPresenceState } from '@/common/components/presence/types';

export const PRESENCE_BAND_ACTIVE_HEIGHT = 120;
export const PRESENCE_BAND_EXIT_MS = 280;
export const PRESENCE_PHRASE_CYCLE_MS = 3800;
export const PRESENCE_SHIMMER_CYCLE_MS = 3600;
export const PRESENCE_PHRASE_CROSSFADE_MS = 200;

export type AiTutorPresenceStatusMode =
  | 'evaluating'
  | 'writingFeedback'
  | 'extendedWait'
  | 'response'
  | 'error';

export type AiTutorPresenceViewModel = {
  orbState: AIPresenceState;
  statusMode: AiTutorPresenceStatusMode;
};

export const STAGE1_PHRASE_KEYS = [
  'student.task.aiTutor.presenceBand.readingDraft',
  'student.task.aiTutor.presenceBand.followingLineOfThought',
  'student.task.aiTutor.presenceBand.checkingPiecesConnect',
] as const;

export const STAGE2_PHRASE_KEYS = [
  'student.task.aiTutor.presenceBand.planningNextMove',
  'student.task.aiTutor.presenceBand.shapingClearSuggestion',
] as const;

export function deriveAiTutorPresence(
  progress: EvaluationStreamProgress | null,
  options?: { errorMessage?: string | null; hasCompleteFeedback?: boolean },
): AiTutorPresenceViewModel {
  if (options?.errorMessage) {
    return {
      orbState: 'error',
      statusMode: 'error',
    };
  }

  if (options?.hasCompleteFeedback) {
    return {
      orbState: 'ready',
      statusMode: 'response',
    };
  }

  if (progress?.stage === 'stage2') {
    return {
      orbState: 'speaking',
      statusMode: 'writingFeedback',
    };
  }

  if (
    progress
    && progress.elapsedMs > EXTENDED_WAIT_THRESHOLD_MS
  ) {
    return {
      orbState: 'thinking',
      statusMode: 'extendedWait',
    };
  }

  return {
    orbState: 'thinking',
    statusMode: 'evaluating',
  };
}

export function deriveStageAccessibilityLabelKey(
  statusMode: AiTutorPresenceStatusMode,
): string | null {
  if (statusMode === 'evaluating') return STAGE1_PHRASE_KEYS[0];
  if (statusMode === 'writingFeedback') return STAGE2_PHRASE_KEYS[0];
  if (statusMode === 'extendedWait') return 'student.task.aiTutor.presenceBand.takingLongerWithDraft';
  if (statusMode === 'error') return 'student.task.aiTutor.presenceBand.evaluationFailed';
  return null;
}

export function deriveActivePresencePhraseKey(
  statusMode: AiTutorPresenceStatusMode,
  elapsedMs: number,
  reducedMotion: boolean,
): string | null {
  if (statusMode === 'extendedWait') {
    return 'student.task.aiTutor.presenceBand.takingLongerWithDraft';
  }
  if (statusMode === 'evaluating') {
    if (reducedMotion) return STAGE1_PHRASE_KEYS[0];
    const index = Math.floor(elapsedMs / PRESENCE_PHRASE_CYCLE_MS) % STAGE1_PHRASE_KEYS.length;
    return STAGE1_PHRASE_KEYS[index];
  }
  if (statusMode === 'writingFeedback') {
    if (reducedMotion) return STAGE2_PHRASE_KEYS[0];
    const index = Math.floor(elapsedMs / PRESENCE_PHRASE_CYCLE_MS) % STAGE2_PHRASE_KEYS.length;
    return STAGE2_PHRASE_KEYS[index];
  }
  return null;
}

export function deriveCompactOrbState(params: {
  isEvaluating: boolean;
  progress: EvaluationStreamProgress | null;
  errorMessage?: string | null;
  hasCompleteFeedback?: boolean;
}): AIPresenceState {
  if (params.errorMessage) return 'error';
  if (params.hasCompleteFeedback && !params.isEvaluating) return 'ready';
  if (params.isEvaluating) {
    return deriveAiTutorPresence(params.progress).orbState;
  }
  return 'idle';
}
